import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import appLogo from "@/assets/logo.png";

/**
 * Animated splash screen that shows on app launch.
 * Uses framer-motion for smooth logo animation with scale + fade.
 * Auto-hides after animation completes (~2.5s).
 */
const AnimatedSplash = ({ onComplete }: { onComplete: () => void }) => {
  const [show, setShow] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(false);
      setTimeout(onComplete, 500); // wait for exit animation
    }, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
          style={{ background: "linear-gradient(135deg, #222a68 0%, #048a81 100%)" }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          {/* Animated logo */}
          <motion.img
            src={appLogo}
            alt="Paróquia Viva"
            className="w-32 h-32 rounded-3xl shadow-2xl"
            initial={{ scale: 0.3, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              delay: 0.2,
            }}
          />

          {/* App name */}
          <motion.h1
            className="text-white text-2xl font-bold mt-6 tracking-wide"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            Paróquia Viva
          </motion.h1>

          {/* Tagline */}
          <motion.p
            className="text-white/70 text-sm mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
          >
            Sua fé, sempre conectada
          </motion.p>

          {/* Pulse ring behind logo */}
          <motion.div
            className="absolute w-40 h-40 rounded-full border-2 border-white/20"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: [0.8, 1.5], opacity: [0.4, 0] }}
            transition={{
              duration: 1.5,
              delay: 0.5,
              repeat: 1,
              ease: "easeOut",
            }}
            style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", marginTop: "-80px" }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnimatedSplash;
